import { loadingActions } from 'modules/loading';
import { ERROR_LOADING_DATA } from 'constants/global';
import LoyaltyPointsApi from './repository';
import { loyaltyPointsActions } from '.';

const manageResponse = (response) => {
  if (response.code === '200' && response.msg === 'OK') {
    return response.obj_data;
  }
  throw new Error(response?.msg || true);
};

const isEmptyCouponType = (value) => (
  value === null || value === undefined || value === ''
);

/**
 * tipo_cupon es numérico y des_tipo_cupon es el texto a mostrar.
 * No se traduce el número a Empeño/Comercialización.
 * En el histórico, ambos vacíos se marcan como legado.
 */
const readCouponType = (item, legacy) => {
  const description = typeof item.des_tipo_cupon === 'string'
    ? item.des_tipo_cupon.trim()
    : '';
  const hasTypeId = typeof item.tipo_cupon === 'number'
    && Number.isFinite(item.tipo_cupon);

  return {
    couponType: description,
    couponTypeId: hasTypeId ? item.tipo_cupon : null,
    legacyCouponType: Boolean(
      legacy
      && !description
      && isEmptyCouponType(item.tipo_cupon)
      && isEmptyCouponType(item.des_tipo_cupon),
    ),
  };
};

/** El backend ya decide el texto. Sin contenido no se muestra. */
const readAutosMessage = (item) => {
  if (typeof item.mensaje_ramo_autos !== 'string') {
    return '';
  }
  return item.mensaje_ramo_autos.trim();
};

/**
 * BonoBienvenida es el acumulado del bono, no un abono recién hecho.
 * 0 es válido. Si falta o no es un número finito, no se convierte en 0.
 */
export const readWelcomeBonus = (data) => {
  const value = data?.BonoBienvenida;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
};

export const formatCoupon = (coupon, promotions = []) => ({
  id: coupon.id_cupon_cliente || null,
  promotionId: coupon.id_cupon || null,
  clientId: coupon.id_cliente || null,
  code: coupon.codigo_cupon || null,
  description: coupon.des_cupon || (promotions || []).find((p) => p.id === coupon.id_cupon)?.description || `Cupón ${coupon.id_cupon}`,
  discount: coupon.descuento || 0,
  endDate: coupon.fecha_vigencia,
  maxAmount: coupon.monto_maximo || 0,
  status: coupon.estatus || 0,
  statusName: coupon.des_estatus || '',
  sections: [
    coupon.ramo_relojes ? 'WATCHES' : null,
    (coupon.ramo_alhaja || coupon.ramo_alhajas) ? 'JEWELRY' : null,
    coupon.ramo_varios ? 'OTHERS' : null,
    coupon.ramo_autos ? 'AUTOS' : null,
  ].filter(Boolean),
  autosMessage: readAutosMessage(coupon),
  ...readCouponType(coupon, true),
});

export const formatPromotion = (promotion, coupons = []) => {
  return {
    id: promotion.id_cupon || null,
    discount: promotion.descuento || 0,
    description: promotion.des_cupon || '',
    points: promotion.puntos || 0,
    endDate: promotion.fecha_final,
    maxAmount: promotion.monto_maximo || 0,
    sections: [
      promotion.ramo_relojes ? 'WATCHES' : null,
      promotion.ramo_alhajas ? 'JEWELRY' : null,
      promotion.ramo_varios ? 'OTHERS' : null,
      promotion.ramo_autos ? 'AUTOS' : null,
    ].filter(Boolean),
    couponId: (coupons || []).find((c) => c.promotionId === promotion.id_cupon)?.id || null,
    autosMessage: readAutosMessage(promotion),
    ...readCouponType(promotion, false),
  };
};

export const getPoints = ({ blocking = true } = {}) => async (dispatch) => {
  if (blocking) {
    dispatch(loadingActions.show());
  }
  dispatch(loyaltyPointsActions.initPoints());

  return LoyaltyPointsApi.getPoints()
    .then((response) => {
      const data = manageResponse(response);
      dispatch(loyaltyPointsActions.successPoints({
        used: data?.Utilizados || 0,
        available: data?.Saldo || 0,
        cancelled: data?.Cancelados || 0,
        generated: data?.Generados || 0,
        expired: data?.Vencidos || 0,
        welcomeBonus: readWelcomeBonus(data),
      }));
      return true;
    })
    .catch((error) => {
      dispatch(loyaltyPointsActions.errorPoints(error?.message || ERROR_LOADING_DATA));
      return false;
    }).finally(() => {
      if (blocking) {
        dispatch(loadingActions.hide());
      }
    });
};

export const getCoupons = (promotions = null) => async (dispatch) => {
  dispatch(loadingActions.show());
  return LoyaltyPointsApi.getCoupons()
    .then(async (response) => {
      const data = manageResponse(response) || [];
      let auxPromotions = promotions ? structuredClone(promotions) : null;
      if (!promotions) {
        // eslint-disable-next-line no-use-before-define
        auxPromotions = await dispatch(getPromotions(data));
        if (!auxPromotions) {
          throw new Error(ERROR_LOADING_DATA);
        }
      }
      const formattedCoupons = data.map((coupon) => formatCoupon(coupon, auxPromotions));
      dispatch(loyaltyPointsActions.successCoupons(formattedCoupons));
      return formattedCoupons;
    })
    .catch((error) => {
      dispatch(loyaltyPointsActions.errorCoupons(error?.message || ERROR_LOADING_DATA));
      return false;
    }).finally(() => {
      dispatch(loadingActions.hide());
    });
};

export const getPromotions = (coupons = null) => async (dispatch) => {
  dispatch(loadingActions.show());
  return LoyaltyPointsApi.getPromotions()
    .then(async (response) => {
      const data = manageResponse(response) || [];
      let auxCoupons = coupons ? structuredClone(coupons) : null;
      if (!auxCoupons) {
        // eslint-disable-next-line no-use-before-define
        auxCoupons = await dispatch(getCoupons(data));
        if (!auxCoupons) {
          throw new Error(ERROR_LOADING_DATA);
        }
      }
      const formattedPromotions = data.map((promotion) => formatPromotion(promotion, auxCoupons));
      dispatch(loyaltyPointsActions.successPromotions(formattedPromotions));
      return formattedPromotions;
    })
    .catch((error) => {
      dispatch(loyaltyPointsActions.errorPromotions(error?.message || ERROR_LOADING_DATA));
      return false;
    }).finally(() => {
      dispatch(loadingActions.hide());
    });
};

export const createCoupon = (id) => async (dispatch) => {
  dispatch(loadingActions.show());
  return LoyaltyPointsApi.createPromotion(id)
    .then((response) => {
      const data = manageResponse(response);
      dispatch(loyaltyPointsActions.initCoupons());
      return formatCoupon(data);
    })
    .catch((error) => {
      throw error;
    }).finally(() => {
      dispatch(loadingActions.hide());
    });
};
